import {
  Container,
  Box,
  Stack,
  SimpleGrid,
  useToast,
  Spinner,
  VStack,
  Text,
  Divider,
  useBreakpointValue,
  Flex
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase/config';
import { signout } from '../firebase/auth';
import { getSiteProfile, getUserSites } from '../firebase/siteProfile'; // Removed getUserReputation
import { getUserReputationData, formatReputationBreakdown } from '../firebase/userReputation'; // Import new reputation functions
import { getUserNotifications } from '../firebase/siteDiscussions';
import { getDocs, collection, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import { AUTO_DETECT_MAPPING } from '../constants/autoDetectMapping';
import { mediaHasFeature, mapFeature, normalizeFeature } from '../utils/featureUtils';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import DashboardStats from '../components/dashboard/DashboardStats';
import NotificationsSection from '../components/dashboard/NotificationsSection';
import RecentActivitySection from '../components/dashboard/RecentActivitySection';
import SitesSection from '../components/dashboard/SitesSection';
import DraftsSection from '../components/dashboard/DraftsSection';

import { getDrafts } from '../firebase/siteDrafts';

export default function Dashboard() {
  const navigate = useNavigate();
  const toast = useToast();
  
  // State for different sections
  const [statsState, setStatsState] = useState({
    data: {
      submittedSites: 0,
      comments: 0,
      reputation: 0,
      reputationBreakdown: '', // Added for detailed breakdown
      drafts: 0
    },
    loading: true
  });

  const [draftsState, setDraftsState] = useState({
    drafts: [],
    loading: true
  });

  const [recentActivity, setRecentActivity] = useState({
    comments: [],
    loading: true,
    currentPage: 1,
    totalComments: 0
  });

  const [notifications, setNotifications] = useState({
    siteComments: [],
    commentReplies: [],
    loading: true,
    currentPage: 1,
    unreadCount: 0
  });

  const [sitesState, setSitesState] = useState({
    allSites: [],
    filteredSites: [],
    filters: { features: [], countries: [] },
    sortBy: 'name',
    featuresMetadata: {},
    loading: false, // Start as false since we'll lazy load
    loaded: false // Track if sites have been loaded
  });

  // Theme - bgGradient removed, background handled by Box styling below

  // Handlers
  const handleFilterChange = (newFilters) => {
    setSitesState(prev => ({
      ...prev,
      filters: { ...prev.filters, ...newFilters }
    }));
  };

  const handleFilterReset = () => {
    setSitesState(prev => ({
      ...prev,
      filters: { features: [], countries: [] }
    }));
  };

  const handleSortChange = (newSort) => {
    setSitesState(prev => ({
      ...prev,
      sortBy: newSort
    }));
  };

  // Main data fetching effect
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        // Start essential fetches in parallel (excluding sites for lazy loading)
        const [
          sitesCountResponse,
          reputationResponse,
          notificationsResponse,
          userCommentsSnap,
          userDraftsResponse
        ] = await Promise.all([
          // Get only the count of sites for stats, not the full data
          getDocs(query(
            collection(db, 'sites'),
            where('submittedBy', '==', user.uid)
          )),
          getUserReputationData(user.uid), // Use new function
          getUserNotifications(user.uid),
          getDocs(query(
            collection(db, 'site_discussions'),
            where('userId', '==', user.uid),
            orderBy('createdAt', 'desc')
          )),
          getDrafts() // Get user drafts
        ]);

        // Process comments and activity
        const recentComments = userCommentsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          type: doc.data().replyTo ? 'reply' : 'site'
        }));

        setRecentActivity({
          comments: recentComments,
          loading: false,
          currentPage: 1,
          totalComments: userCommentsSnap.size
        });

        // Process notifications
        if (notificationsResponse.success) {
          const unreadCount = 
            notificationsResponse.data.siteComments.filter(n => !n.read).length +
            notificationsResponse.data.commentReplies.filter(n => !n.read).length;

          setNotifications({
            ...notificationsResponse.data,
            unreadCount,
            loading: false,
            currentPage: 1
          });
        }

        // Process drafts
        if (userDraftsResponse.success) {
          setDraftsState({
            drafts: userDraftsResponse.data,
            loading: false
          });
        }

        // Update stats
        setStatsState({
          data: {
            submittedSites: sitesCountResponse.size, // Use the count from the query
            comments: userCommentsSnap.size,
            reputation: reputationResponse.success ? reputationResponse.data.totalPoints : 0,
            reputationBreakdown: reputationResponse.success ? formatReputationBreakdown(reputationResponse.data) : '',
            drafts: userDraftsResponse.success ? userDraftsResponse.data.length : 0
          },
          loading: false
        });

      } catch (error) {
        console.error('Error fetching user data:', error);
        toast({
          title: "Error loading dashboard",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    });

    return () => unsubscribe();
  }, [navigate, toast]);

  // Filter effect
  useEffect(() => {
    let filtered = [...sitesState.allSites];
    
    if (sitesState.filters.features?.length > 0) {
      filtered = filtered.filter(site => 
        Array.isArray(site.media) && sitesState.filters.features.every(featureId =>
          site.media.some(media => mediaHasFeature(media, featureId))
        )
      );
    }
    
    if (sitesState.filters.countries?.length > 0) {
      filtered = filtered.filter(site => 
        site.location?.country && sitesState.filters.countries.includes(site.location.country)
      );
    }
    
    setSitesState(prev => ({
      ...prev,
      filteredSites: filtered
    }));
  }, [sitesState.filters, sitesState.allSites]);

  // Lazy load sites function
  const handleLoadSites = async () => {
    if (sitesState.loaded || sitesState.loading) return;

    setSitesState(prev => ({ ...prev, loading: true }));

    try {
      const user = auth.currentUser;
      if (!user) return;

      const sitesResponse = await getUserSites(user.uid);

      if (sitesResponse.success) {
        const sites = sitesResponse.data;
        // Process sites and create feature metadata
        const featureMap = new Map();
        
        for (const site of sites) {
          if (!Array.isArray(site.media)) continue;
          
          for (const media of site.media) {
            if (!Array.isArray(media?.predictions)) continue;
            
            const features = media.predictions
              .filter(pred => pred && (!pred.source || pred.source === 'manual' || (pred.source === 'auto' && pred.confidence > 0.5)))
              .map(pred => {
                const { featureId, label } = mapFeature(pred);
                return {
                  id: featureId,
                  label,
                  source: pred.source || 'manual',
                  confidence: pred.confidence
                };
              });
            
            for (const feature of features) {
              if (feature.id) {
                featureMap.set(feature.id, feature);
              }
            }
          }
        }

        setSitesState(prev => ({
          ...prev,
          allSites: sites,
          filteredSites: sites,
          featuresMetadata: Object.fromEntries(featureMap),
          loading: false,
          loaded: true
        }));
      } else {
        toast({
          title: "Error loading sites",
          description: sitesResponse.error,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setSitesState(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error loading sites:', error);
      toast({
        title: "Error loading sites",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setSitesState(prev => ({ ...prev, loading: false }));
    }
  };

  // Handlers
  const handleSignOut = async () => {
    try {
      await signout();
      navigate('/login');
    } catch (error) {
      toast({
        title: "Error signing out",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Responsive spacing and layout
  const containerPadding = useBreakpointValue({ base: 4, md: 6, lg: 8 });
  const sectionSpacing = useBreakpointValue({ base: 6, md: 8 });
  const isMobile = useBreakpointValue({ base: true, md: false });

  return (
    <Box
      // Apply parchment texture background using CSS variable via _before pseudo-element
      _before={{
        content: '""',
        position: 'fixed', // Use fixed to cover viewport regardless of scroll
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bg: 'bg.default', // Match background color for consistency
        zIndex: -1, // Place behind content
        pointerEvents: 'none',
        backgroundImage: 'none', // Default no texture
        opacity: 0,
        transition: 'opacity 0.3s ease-in-out',
        'body.textures-enabled &': { // Apply texture only when enabled
          backgroundImage: 'var(--texture-dashboard-bg)', // Use parchment texture variable
          backgroundRepeat: 'repeat',
          backgroundSize: isMobile ? '200px' : '300px', // Smaller texture on mobile
          backgroundAttachment: 'fixed',
          backgroundBlendMode: 'soft-light', // Adjust blend mode as needed
          opacity: isMobile ? 0.1 : 0.15, // Lighter on mobile for better readability
        },
        'body.textures-disabled &': { // Explicitly disable
          backgroundImage: 'none',
          opacity: 0,
        },
      }}
      minH="100vh"
      py={containerPadding}
      position="relative"
      overflow="hidden"
      // Add safe area support for mobile
      paddingBottom={isMobile ? 'env(safe-area-inset-bottom)' : containerPadding}
    >
      <Container 
        maxW="container.xl" 
        position="relative" 
        zIndex={1} 
        bg="transparent"
        px={containerPadding}
      >
        <VStack spacing={sectionSpacing} align="stretch">
          <DashboardHeader
            onSignOut={handleSignOut}
            userName={auth.currentUser?.displayName}
            isLoading={statsState.loading}
          />
          
          <DashboardStats
            stats={statsState.data}
            isLoading={statsState.loading}
          />

          {/* Reorganized Dashboard Layout */}
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={sectionSpacing} alignItems="start">
            {/* Column 1: Notifications and Drafts */}
            <VStack spacing={sectionSpacing} align="stretch">
              <NotificationsSection
                notifications={notifications}
                onNotificationUpdate={(updatedNotification) => {
                  setNotifications(prev => {
                    const newState = { ...prev };
                    const siteCommentIndex = newState.siteComments.findIndex(n => n.id === updatedNotification.id);
                    if (siteCommentIndex !== -1) {
                      newState.siteComments[siteCommentIndex] = updatedNotification;
                      if (updatedNotification.read) newState.unreadCount = Math.max(0, newState.unreadCount - 1);
                      return newState;
                    }
                    const commentReplyIndex = newState.commentReplies.findIndex(n => n.id === updatedNotification.id);
                    if (commentReplyIndex !== -1) {
                      newState.commentReplies[commentReplyIndex] = updatedNotification;
                      if (updatedNotification.read) newState.unreadCount = Math.max(0, newState.unreadCount - 1);
                      return newState;
                    }
                    return prev;
                  });
                }}
              />
              <DraftsSection
                drafts={draftsState.drafts}
                isLoading={draftsState.loading}
              />
            </VStack>

            {/* Column 2: Recent Activity and Sites */}
            <VStack spacing={sectionSpacing} align="stretch">
              <RecentActivitySection recentActivity={recentActivity} />
              <Divider variant="dashboardDivider" display={{ base: 'block', lg: 'none' }} /> {/* Show divider on mobile, hide on lg */}
              <SitesSection
                allUserSites={sitesState.allSites}
                filteredSites={sitesState.filteredSites}
                filters={sitesState.filters}
                onFilterChange={handleFilterChange}
                onFilterReset={handleFilterReset}
                sortBy={sitesState.sortBy}
                onSortChange={handleSortChange}
                featuresMetadata={sitesState.featuresMetadata}
                normalizeFeature={normalizeFeature}
                isLoading={sitesState.loading}
                onLoadSites={handleLoadSites}
                initialSiteCount={statsState.data.submittedSites} 
              />
            </VStack>
          </SimpleGrid>
          
        </VStack>
      </Container>
    </Box>
  );
}
